import type { DatapointLabelsMode } from 'features/charts-v2/types/filter-hash/v2'

/**
 * What a line chart draws for datapoint value labels:
 *
 * - `'all'`        → every point labeled, no culling (single-series charts,
 *                    or the explicit `'on'` override).
 * - `'culled'`     → every point labeled, then a pixel-space occlusion pass
 *                    hides colliding labels so only the topmost of each
 *                    cluster survives (see selectCulledLabelIndexes).
 * - `'hover-only'` → no static labels; the hovered series' labels reveal
 *                    imperatively (see applyLineHoverEmphasis).
 */
export type PointLabelDisplay = 'all' | 'culled' | 'hover-only'

/**
 * Resolve the panel's datapoint-label mode against the number of series
 * actually drawn. `'on'` means "as many labels as fit cleanly": every point
 * on single-series charts (one label per bucket can't collide), occlusion-
 * culled labels on multi-series charts — labeling every point of every
 * series degrades into an unreadable smear as series pile up at each
 * x-bucket, so there is deliberately no label-everything mode. `'off'` is
 * hover-only.
 */
export function resolvePointLabelDisplay(
  mode: DatapointLabelsMode,
  seriesCount: number,
): PointLabelDisplay {
  if (mode === 'off') {
    // Hover-only; a single-series chart renders this as no labels at all
    // (it has no hover-emphasis machinery).
    return 'hover-only'
  }
  return seriesCount <= 1 ? 'all' : 'culled'
}

export function shouldRenderPointLabels(display: PointLabelDisplay): boolean {
  return display !== 'hover-only'
}
